import {
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormDialog,
  FormDialogCancel,
} from "@capacit-com/lara-law-shared-ui";
import { Item } from "@/pages/workspace/utils/workspaceItemUtils";
import Tenant from "@/models/tenant/tenant";
import { Button } from "@capacit-com/lara-law-shared-ui";
import {
  WorkspaceDocumentMetadata,
  TenantDocumentMetadata,
} from "@/models/metadata/metadata";
import { FC, useEffect, useState, useMemo, useRef } from "react";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import useWorkspace from "@/hooks/useWorkspace";
import {
  extractMultipleDocxCustomProperties,
  DocxMetadataExtractionResult,
} from "@/services/docxMetadataService";
import { useUploadMetadataStore } from "@/stores/upload-metadata.store";
import { showSuccessToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import MetadataDialogContent from "./MetadataDialogContent";
import {
  cleanMetadata,
  hasValidMetadata,
  serializeMetadataListForCompare,
} from "./helpers";
import { AlertTriangle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import useTenant from "@/hooks/useTenant";
import { useOverflowObserver } from "@/hooks/useOverflowObserver";
import { useDocumentMetadata } from "@/hooks/useWidgetData";

interface MetadataFormDialogProps {
  editDocument: Item | null;
  onClose: () => void;
  workspaceId: string;
  isUploadMode?: boolean;
  uploadFiles?: FileList;
  onUploadConfirm?: (
    metadata: WorkspaceDocumentMetadata[][]
  ) => Promise<void> | void;
}

const EMPTY_STORED_METADATA: WorkspaceDocumentMetadata[] = [];

interface MetadataFieldLabelProps {
  name: string;
  externalPropertyName: string;
}

const MetadataFieldLabel: FC<MetadataFieldLabelProps> = ({
  name,
  externalPropertyName,
}) => {
  const nameRef = useRef<HTMLDivElement>(null);
  const externalNameRef = useRef<HTMLDivElement>(null);

  const isNameOverflowing = useOverflowObserver(nameRef, [name]);
  const isExternalNameOverflowing = useOverflowObserver(externalNameRef, [
    externalPropertyName,
  ]);

  return (
    <div className="min-w-0 max-w-35 sm:max-w-60">
      <Tooltip>
        <TooltipTrigger asChild>
          <div ref={nameRef} className="text-md truncate">
            {name}
          </div>
        </TooltipTrigger>
        {isNameOverflowing && (
          <TooltipContent>
            <p>{name}</p>
          </TooltipContent>
        )}
      </Tooltip>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            ref={externalNameRef}
            className="text-xs text-muted-foreground truncate"
          >
            {externalPropertyName}
          </div>
        </TooltipTrigger>
        {isExternalNameOverflowing && (
          <TooltipContent>
            <p>{externalPropertyName}</p>
          </TooltipContent>
        )}
      </Tooltip>
    </div>
  );
};

const MetadataFormDialog: FC<MetadataFormDialogProps> = ({
  editDocument,
  onClose,
  workspaceId,
  isUploadMode = false,
  uploadFiles,
  onUploadConfirm,
}) => {
  const { updateDocumentMetadata } = useWorkspace();
  const { useQueryCurrent } = useTenant();
  const tenantQuery = useQueryCurrent();
  // Suggested values from all workspaces in the tenant (no workspace filter)
  const { data: discoveredMetadata } = useDocumentMetadata(undefined);
  const [tenant, setTenant] = useState<Tenant>();
  const [allFields, setAllFields] = useState<
    (WorkspaceDocumentMetadata | TenantDocumentMetadata)[]
  >([]);

  useEffect(() => {
    if (tenantQuery.isFetched && tenantQuery.data) {
      setTenant(tenantQuery.data);
    }
  }, [tenantQuery.isFetched, tenantQuery.data]);
  // Use the upload metadata store
  const {
    currentDocumentIndex,
    totalDocuments,
    setCurrentDocumentIndex,
    setTotalDocuments,
    setUploadFiles,
    goToNextDocument,
    goToPreviousDocument,
    getCurrentDocument,
    getCurrentDocumentName,
    getAllMetadata,
    clearAll,
    initializeDocumentMetadata,
    getFieldValue,
    setFieldValue,
    deleteField,
    getDocumentMetadata,
    setCurrentDocumentName,
  } = useUploadMetadataStore();

  // Get current document name for metadata lookup
  const currentDocumentName = isUploadMode
    ? getCurrentDocumentName()
    : editDocument?.name;

  const [docxExtractionResults, setDocxExtractionResults] = useState<
    DocxMetadataExtractionResult[]
  >([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tenantFields = tenant?.tenantDocumentMetadataList || [];

  const editDocumentLiveMetadata = useUploadMetadataStore((s) =>
    !isUploadMode && editDocument
      ? (s.documentMetadata[editDocument.name] ?? EMPTY_STORED_METADATA)
      : EMPTY_STORED_METADATA
  );

  const isEditMetadataStoreSeeded = useUploadMetadataStore((s) => {
    if (isUploadMode || !editDocument) {
      return true;
    }
    const initialList = editDocument.documentMetadataList ?? [];
    if (initialList.length === 0) {
      return true;
    }
    const stored = s.documentMetadata[editDocument.name];
    return stored !== undefined && stored.length > 0;
  });

  const editMetadataBaselineSerialized = useMemo(() => {
    if (isUploadMode || !editDocument) {
      return null;
    }
    return serializeMetadataListForCompare(
      editDocument.documentMetadataList ?? [],
      tenantFields
    );
  }, [isUploadMode, editDocument, tenantFields]);

  const isEditMetadataDirty = useMemo(() => {
    if (
      isUploadMode ||
      !editDocument ||
      editMetadataBaselineSerialized === null ||
      !isEditMetadataStoreSeeded
    ) {
      return false;
    }
    return (
      serializeMetadataListForCompare(
        editDocumentLiveMetadata,
        tenantFields
      ) !== editMetadataBaselineSerialized
    );
  }, [
    isUploadMode,
    editDocument,
    editDocumentLiveMetadata,
    editMetadataBaselineSerialized,
    tenantFields,
    isEditMetadataStoreSeeded,
  ]);
  const documentFields = editDocument?.documentMetadataList || [];

  useEffect(() => {
    if (tenantQuery.isFetched && tenantQuery.data) {
      setTenant(tenantQuery.data);
    }
  }, [tenantQuery.isFetched, tenantQuery.data]);

  useEffect(() => {
    // Combine all fields, prioritizing tenant fields when both exist
    const allFields = [
      // Add tenant fields first (these represent the current organization configuration)
      ...tenantFields,
      // Add workspace fields that don't exist in tenant (workspace-only fields)
      ...documentFields.filter(
        (d) => !tenantFields.some((t) => t.name === d.name)
      ),
    ];

    // Sort fields alphabetically by name
    const sortedFields = allFields.sort((a, b) => a.name.localeCompare(b.name));
    setAllFields(sortedFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, editDocument]);

  // Initialize store when component mounts or upload files change
  useEffect(() => {
    if (isUploadMode && uploadFiles) {
      setUploadFiles(uploadFiles);
      setTotalDocuments(uploadFiles.length);
      setCurrentDocumentIndex(0);
    } else if (!isUploadMode && editDocument) {
      // For edit mode, clear store first and set up for a single document
      clearAll();
      setTotalDocuments(1);
      setCurrentDocumentIndex(0);
      setCurrentDocumentName(editDocument.name);
    }
  }, [
    isUploadMode,
    uploadFiles,
    editDocument,
    setUploadFiles,
    setTotalDocuments,
    setCurrentDocumentIndex,
    clearAll,
    setCurrentDocumentName,
  ]);

  // Initialize values for all documents
  useEffect(() => {
    if (isUploadMode && uploadFiles) {
      // Initialize metadata for all uploaded files
      Array.from(uploadFiles).forEach((file) => {
        const extractionResult = docxExtractionResults.find(
          (res) => res.fileName === file.name
        );

        if (extractionResult?.hasCustomProperties) {
          initializeDocumentMetadata(file.name, "upload", {
            extractionResult,
            tenantFields,
          });
        } else {
          // Initialize with empty metadata for non-DOCX files or DOCX files without custom properties
          initializeDocumentMetadata(file.name, "upload", {
            tenantFields,
          });
        }
      });
    } else if (
      !isUploadMode &&
      editDocument?.documentMetadataList &&
      currentDocumentName
    ) {
      // For edit mode, initialize with existing document metadata
      initializeDocumentMetadata(currentDocumentName, "edit", {
        editDocument: {
          documentMetadataList: editDocument.documentMetadataList,
        },
        tenantFields,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentDocumentName,
    editDocument,
    docxExtractionResults,
    isUploadMode,
    tenant,
    initializeDocumentMetadata,
    uploadFiles,
  ]);

  useEffect(() => {
    if (isUploadMode && uploadFiles?.length) {
      (async () => {
        try {
          // Extract metadata from DOCX files if any exist
          const docxFiles = Array.from(uploadFiles).filter((f) =>
            f.name.toLowerCase().endsWith(".docx")
          );
          if (docxFiles.length) {
            setDocxExtractionResults(
              await extractMultipleDocxCustomProperties(docxFiles)
            );
          } else {
            // No DOCX files, but still allow metadata entry for all files
            setDocxExtractionResults([]);
          }
        } catch (error) {
          logger.warn("Failed to extract metadata from uploaded DOCX files", {
            operation: "extractUploadDocxMetadata",
            error,
            fileCount: uploadFiles.length,
          });
        }
      })();
    }
  }, [isUploadMode, uploadFiles]);

  // Cleanup store when dialog closes
  useEffect(() => {
    return () => {
      if (!isUploadMode) {
        // Clear store when edit dialog closes
        clearAll();
      }
    };
  }, [isUploadMode, clearAll]);

  // Check for custom properties that don't match tenant fields
  const unmatchedProperties = useMemo(() => {
    if (!isUploadMode || !currentDocumentName) return [];

    const currentFile = getCurrentDocument();
    if (!currentFile) return [];

    const extractionResult = docxExtractionResults.find(
      (res) => res.fileName === currentFile.name
    );

    if (!extractionResult?.hasCustomProperties) return [];

    // Find custom properties that don't have a matching tenant field
    const unmatched = extractionResult.customProperties.filter(
      (prop) =>
        !tenantFields.some(
          (field) =>
            field.externalPropertyName?.toLowerCase() ===
            prop.name.toLowerCase()
        )
    );

    return unmatched;
  }, [
    isUploadMode,
    currentDocumentName,
    docxExtractionResults,
    tenantFields,
    getCurrentDocument,
  ]);

  const handleDeleteField = (name: string) => {
    deleteField(name);

    // After deleting a field, refresh the field list to maintain proper prioritization
    setAllFields((f) => {
      const filtered = f.filter((x) => x.name !== name);
      // Re-add tenant field if it exists (tenant fields take priority)
      const tenantField = tenantFields.find((t) => t.name === name);
      if (tenantField) {
        const updated = [...filtered, tenantField];
        return updated.sort((a, b) => a.name.localeCompare(b.name));
      }
      return filtered.sort((a, b) => a.name.localeCompare(b.name));
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isUploadMode && onUploadConfirm) {
      // Get metadata for all documents from store and clean it (remove invalid values)
      const allMetadata = getAllMetadata();
      const cleanedMetadata = allMetadata.map((documentMetadata) =>
        documentMetadata
          .map((obj) => ({
            ...obj,
            metadata: cleanMetadata(obj.metadata, obj.name, tenantFields) || {},
          }))
          .filter((x) => hasValidMetadata(x.metadata))
      );
      onUploadConfirm(cleanedMetadata);
      onClose();
      return;
    }

    if (!workspaceId || !editDocument?.id || !editDocument.documentSource)
      return;

    // For edit mode, use the current document's metadata from the store
    const documentMetadata = currentDocumentName
      ? getDocumentMetadata(currentDocumentName)
      : [];

    await updateDocumentMetadata.mutateAsync({
      workspaceId,
      documentId: editDocument.id,
      requestBody: {
        documentMetadataList: documentMetadata
          .map((obj) => ({
            id: obj.id,
            name: obj.name,
            externalPropertyName: obj.externalPropertyName,
            metadata: cleanMetadata(obj.metadata, obj.name, tenantFields) || {},
          }))
          .filter((x) => hasValidMetadata(x.metadata)),
        documentSource: editDocument.documentSource,
        driveId: editDocument.driveId,
        siteId: editDocument.siteId,
        sharepointFileId: editDocument.sharepointFileId,
      },
    });
    showSuccessToast("Filens metadata er blevet opdateret");
    onClose();
  };

  // Closing an upload dialog always means dropping the whole upload, so the
  // guard is armed for the entire upload flow with its own copy. Edit mode
  // only asks once the metadata differs from what was loaded.
  const isDirty = isUploadMode || isEditMetadataDirty;
  const discardConfirm = isUploadMode
    ? {
        title: "Afbryd upload?",
        description: "Uploaden stoppes, og ingen af filerne bliver uploadet.",
        discardLabel: "Afbryd upload",
        keepLabel: "Annuller",
      }
    : undefined;

  return (
    <FormDialog
      open={editDocument !== null || (isUploadMode && !!uploadFiles)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      isDirty={isDirty}
      discardConfirm={discardConfirm}
      size="lg"
      aria-describedby={undefined}
    >
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <span>
            {isUploadMode
              ? `Udfyld metadata for dokument ${
                  currentDocumentIndex + 1
                } af ${totalDocuments}`
              : "Rediger metadata"}
          </span>
          {unmatchedProperties.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-warning" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-semibold mb-2">
                  Følgende metadata felter fra dokumentet matcher ikke
                  organisationens felter:
                </p>
                <ul className="list-disc list-inside space-y-1">
                  {unmatchedProperties.map((prop) => (
                    <li key={prop.name} className="text-sm">
                      <span className="font-medium">{prop.name}</span>
                      {prop.value && (
                        <span className=""> (værdi: {prop.value})</span>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="text-sm mt-2">
                  Disse værdier kan ikke importeres og bliver ikke tilføjet til
                  dokumentet.
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </DialogTitle>
        {isUploadMode && currentDocumentName && (
          <p className="text-sm text-muted-foreground">
            Dokument: {currentDocumentName}
          </p>
        )}
      </DialogHeader>
      <form onSubmit={handleSubmit} className="contents">
        <DialogBody className="space-y-2">
          {tenantQuery.isPending ? (
            <Spinner />
          ) : allFields.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              {isUploadMode
                ? "Ingen metadata felter er defineret."
                : "Ingen metadata felter er defineret."}
            </p>
          ) : (
            allFields.map((f) => (
              <div
                key={f.name}
                className="flex items-center gap-2 border-t border-border first:border-t-0 py-3 mb-0 justify-between"
              >
                <MetadataFieldLabel
                  name={f.name}
                  externalPropertyName={f.externalPropertyName || ""}
                />
                <div className="flex flex-1 justify-end items-center gap-2 max-w-[60%]">
                  <MetadataDialogContent
                    field={f}
                    value={getFieldValue(f.name)}
                    onChange={(value) =>
                      setFieldValue(
                        f.name,
                        value as
                          | string
                          | number
                          | boolean
                          | Date[]
                          | string[]
                          | undefined,
                        f
                      )
                    }
                    tenantFields={tenantFields}
                    onDelete={() => handleDeleteField(f.name)}
                    isUploadMode={isUploadMode}
                    suggestedValues={
                      discoveredMetadata?.find((m) => m.fieldName === f.name)
                        ?.distinctValues ?? []
                    }
                    docxExtractionResults={docxExtractionResults.map((res) => ({
                      propertyName: res.fileName,
                      value: res.customProperties
                        .map((prop) => prop.value)
                        .join(", "),
                    }))}
                  />
                </div>
              </div>
            ))
          )}
        </DialogBody>
        <DialogFooter className="sm:justify-between">
          <div className="flex gap-2">
            {isUploadMode && totalDocuments > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={goToPreviousDocument}
                disabled={currentDocumentIndex <= 0}
              >
                Forrige
              </Button>
            )}
            {isUploadMode && totalDocuments > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={goToNextDocument}
                disabled={currentDocumentIndex >= totalDocuments - 1}
              >
                Næste
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <FormDialogCancel />
            <Button type="submit" disabled={updateDocumentMetadata.isPending}>
              {isUploadMode
                ? totalDocuments <= 1
                  ? "Upload dokument"
                  : "Upload alle dokumenter"
                : updateDocumentMetadata.isPending
                  ? "Gemmer..."
                  : "Gem metadata"}
            </Button>
          </div>
        </DialogFooter>
      </form>
    </FormDialog>
  );
};

export default MetadataFormDialog;
